"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Shipping() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const features = [{
    title: "Cambridge Research Systems",
    contact_name: "Steven Elliott",
    email: "sales@crsltd.com",
    website: ["www.crsltd.com", "www.crsltd.com/nomoco"]
  }, {
    title: "EEG/fMRI for USA:",
    contact_name: "Brain Vision LLC",
    email: "sales@brainvision.com",
    phone: "+1.877.EEG4MRI / +1.877.334.4674",
    website: "www.brainvision.com"
  }, {
    title: "EEG/fMRI for Canada:",
    contact_name: "Brain Vision Solutions, Inc.",
    email: "sales@bv-solutions.com",
    phone: "+1.514.552.9797",
    website: "www.bv-solutions.com"
  }];
  return <section data-section-id="10954" id="shipping-information" className="bg-background text-foreground">
      <div className="bg-secondary text-foreground border-y border-border py-24 md:py-32">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-4">
              <div ref={headerRef} className={`lg:sticky lg:top-24 transition-all duration-500 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
                <div className="font-mono text-[0.6875rem] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-6">
                  PREFERRED DISTRIBUTORS
                </div>
                <h2 className="font-default font-bold text-[clamp(2rem,3.2vw,3rem)] leading-[1.04] tracking-[-0.02em] text-primary mb-6">
                  Preferred Distributors in Specialized Fields of Neuroimaging
                </h2>
                <p className="font-default text-[0.9375rem] leading-[1.6] text-foreground mb-8">
                  Functional Imaging, including fMRI; and Vision Science for UK, Europe and Worldwide. Advancing our understanding of Vision and the Brain.
                </p>

                <div className="flex flex-col gap-5">
                  <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6 rounded-md font-bold text-sm w-fit">
                    <Link to="/ContactPage">
                      Contact Us
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>

            <div className="lg:col-span-8">
              <div className="border-t border-border">
                {features.map((feature, i) => <div key={i} data-index={i} className="group grid grid-cols-1 md:grid-cols-8 gap-6 md:gap-8 py-10 border-b border-border px-2 -mx-2 transition-colors duration-150 hover:bg-accent/50">
                    <div className="md:col-span-1">
                      <span className="font-mono text-[0.875rem] text-muted-foreground tabular-nums">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                    </div>

                    <div className="md:col-span-7">
                      <h3 className="font-default font-bold text-[clamp(1.25rem,1.6vw,1.625rem)] leading-[1.15] tracking-[-0.01em] text-primary mb-1">
                        {feature.title}
                      </h3>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 pt-6 border-t border-border">
                        {"contact_name" in feature && feature.contact_name && <div className="flex flex-col gap-1">
                            <span className="font-mono text-[0.6875rem] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                              CONTACT
                            </span>
                            <span className="font-default text-[0.875rem] text-foreground">
                              {feature.contact_name}
                            </span>
                          </div>}
                        {feature.email && <div className="flex flex-col gap-1">
                            <span className="font-mono text-[0.6875rem] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                              EMAIL
                            </span>
                            <Link to={`mailto:${feature.email}`} className="font-default text-[0.875rem] text-primary hover:underline">
                              {feature.email}
                            </Link>
                          </div>}
                        {"phone" in feature && feature.phone && <div className="flex flex-col gap-1">
                            <span className="font-mono text-[0.6875rem] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                              PHONE
                            </span>
                            <span className="font-default text-[0.875rem] text-foreground">
                              {feature.phone}
                            </span>
                          </div>}
                        {feature.website && <div className="flex flex-col gap-1">
                            <span className="font-mono text-[0.6875rem] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                              WEBSITE
                            </span>
                            <div className="flex flex-col gap-1">
                              {Array.isArray(feature.website) ? feature.website.map((url, idx) => <Link key={idx} to={url.startsWith('http') ? url : `https://${url}`} newTab className="font-default text-[0.875rem] text-primary hover:underline truncate">
                                    {url.replace('https://', '').replace('http://', '')}
                                  </Link>) : <Link to={feature.website.startsWith('http') ? feature.website : `https://${feature.website}`} newTab className="font-default text-[0.875rem] text-primary hover:underline truncate">
                                  {feature.website.replace('https://', '').replace('http://', '')}
                                </Link>}
                            </div>
                          </div>}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}